import { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowsOutIcon,
  ArrowsInIcon,
  ArrowDownIcon,
  SortDescendingIcon,
  SortAscendingIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { Label } from '../../ui/label';
import { useTransformedQuoteSheetData } from './useTransformedQuoteSheetData';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { QuoteSheetDashboardFilters } from '@/api/procurement/quote-sheets-types';
import { ProductsV2Autocomplete } from '../../products/ProductsV2Autocomplete';
import { useEagerAndDebouncedState } from '@/hooks/useEagerDebouncedState';
import {
  QuoteSheetTableProvider,
  useQuoteSheetTableContext,
} from './quotes-sheet-table.context';
import { ProductVariantBreakdown } from './ProductVariantBreakdown';
import { QuoteSheetDashboardRow } from './QuoteSheetTableRows';
import { Reset } from '@/components/common/semantic-icons';
import {
  AggregatedRowData as AggregatedRowData,
  SupplierRowData as SupplierRowData,
} from './utils';
import { cn } from '@/lib/utils';
import { SupplierAutocomplete } from '@/components/company/SupplierAutocomplete';

// Constants for consistent key composition
const AGGREGATE_KEY_SEPARATOR = ' / ';
const SUPPLIER_KEY_PREFIX = 'supplier';

// Local discriminated interfaces for type safety
interface BaseRowWithChildren {
  key: string;
  children: unknown[];
}

// Type guards for safe property access
function hasChildren(obj: unknown): obj is BaseRowWithChildren {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    'key' in obj &&
    typeof (obj as Record<string, unknown>).key === 'string' &&
    'children' in obj &&
    Array.isArray((obj as Record<string, unknown>).children) &&
    ((obj as Record<string, unknown>).children as unknown[]).length > 0
  );
}

function isSupplier(obj: unknown): obj is SupplierRowData {
  if (
    !hasChildren(obj) ||
    obj == null ||
    typeof obj !== 'object' ||
    !('vendor' in obj)
  ) {
    return false;
  }
  const vendor = (obj as Record<string, unknown>).vendor as Record<
    string,
    unknown
  > | null;
  return (
    vendor !== null &&
    typeof vendor === 'object' &&
    'id' in vendor &&
    'name' in vendor
  );
}

function isLabeled(obj: unknown): obj is AggregatedRowData {
  return (
    hasChildren(obj) &&
    'label' in obj &&
    typeof (obj as Record<string, unknown>).label === 'string'
  );
}

// Helper function to compose aggregate keys consistently
function composeAggregateKey(
  prefix: string[],
  type: 'supplier' | 'labeled',
  key: string,
  label?: string,
): string[] {
  const keyPart =
    type === 'supplier' ? `${SUPPLIER_KEY_PREFIX}-${key}` : `${label}-${key}`;

  return [...prefix, keyPart];
}

// Helper function to extract all aggregate keys from transformed data
function extractAllAggregateKeys(
  data: unknown[],
  aggregateKeyPrefix: string[] = [],
): string[] {
  const keySet = new Set<string>();

  for (const item of data) {
    if (!hasChildren(item)) {
      continue; // Skip items that don't have the expected structure
    }

    // Build the aggregate key for this item using type guards
    let aggregateKey: string[];

    if (isSupplier(item)) {
      aggregateKey = composeAggregateKey(
        aggregateKeyPrefix,
        'supplier',
        item.key,
      );
    } else if (isLabeled(item)) {
      aggregateKey = composeAggregateKey(
        aggregateKeyPrefix,
        'labeled',
        item.key,
        item.label,
      );
    } else {
      continue; // Skip items that don't match expected discriminated types
    }

    const aggregateKeyString = aggregateKey.join(AGGREGATE_KEY_SEPARATOR);
    keySet.add(aggregateKeyString);

    // Recursively get keys from children
    const childKeys = extractAllAggregateKeys(item.children, aggregateKey);
    childKeys.forEach((key) => keySet.add(key));
  }

  return Array.from(keySet);
}

export function QuoteSheetTable() {
  return (
    <QuoteSheetTableProvider>
      <QuoteSheetTableBase />
    </QuoteSheetTableProvider>
  );
}

function QuoteSheetTableBase() {
  const [timePeriod, setTimePeriod] = useState('past-week');
  const {
    showHistoricalData,
    // setShowHistoricalData,
    expandAllAggregates,
    collapseAllAggregates,
    sortBy,
    setSortBy,
  } = useQuoteSheetTableContext();

  // Filter state
  const [filters, setFilters] = useState<QuoteSheetDashboardFilters>({});
  const {
    eagerValue: search,
    debouncedValue: searchDebounced,
    setValue: setSearch,
  } = useEagerAndDebouncedState('', 150);

  // Apply filters including debounced search
  const appliedFilters: QuoteSheetDashboardFilters = {
    ...filters,
    search: searchDebounced || undefined,
  };

  // Apply time period to date filters
  const getDateRange = (): {
    dateFrom: Date;
    dateTo: Date;
  } => {
    const now = new Date();
    const endDate = new Date(now);
    endDate.setHours(23, 59, 59, 999); // End of day

    switch (timePeriod) {
      case 'past-month': {
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        monthAgo.setHours(0, 0, 0, 0);
        return { dateFrom: monthAgo, dateTo: endDate };
      }
      // case 'week-over-week': {
      //   const twoWeeksAgo = new Date(now);
      //   twoWeeksAgo.setDate(now.getDate() - 14);
      //   twoWeeksAgo.setHours(0, 0, 0, 0);
      //   return { dateFrom: twoWeeksAgo, dateTo: endDate };
      // }
      // case 'day-over-day': {
      //   const twoDaysAgo = new Date(now);
      //   twoDaysAgo.setDate(now.getDate() - 2);
      //   twoDaysAgo.setHours(0, 0, 0, 0);
      //   return { dateFrom: twoDaysAgo, dateTo: endDate };
      // }
      case 'past-week':
      default: {
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        weekAgo.setHours(0, 0, 0, 0); // Start of day
        return { dateFrom: weekAgo, dateTo: endDate };
      }
    }
  };

  const dateRange = getDateRange();
  const finalFilters = {
    ...appliedFilters,
    ...dateRange,
  } satisfies QuoteSheetDashboardFilters &
    Required<Pick<QuoteSheetDashboardFilters, 'dateFrom' | 'dateTo'>>;

  const transformedData = useTransformedQuoteSheetData(finalFilters, sortBy);

  // Extract all possible aggregate keys for expand all functionality
  const allAggregateKeys = useMemo(() => {
    return transformedData ? extractAllAggregateKeys(transformedData) : [];
  }, [transformedData]);

  function updateFilter<
    TValue extends QuoteSheetDashboardFilters[keyof QuoteSheetDashboardFilters],
  >(key: keyof QuoteSheetDashboardFilters, value: TValue): void {
    setFilters((prev) => ({
      ...prev,
      [key]: value === '' ? undefined : value,
    }));
  }

  return (
    <>
      <Card>
        <CardContent className="flex flex-col gap-2">
          {/* Filters Section */}
          <div className="py-4 border-b border-border">
            <div className="space-y-4 flex flex-wrap gap-4">
              <div className="flex flex-wrap gap-4 items-end">
                {/* Search */}
                <Label vertical>
                  Search
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search product variants"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </Label>

                {/* Time Period */}
                <Label vertical>
                  Time Period
                  <Select value={timePeriod} onValueChange={setTimePeriod}>
                    <SelectTrigger id="timePeriod">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="past-week">Past Week</SelectItem>
                      <SelectItem value="past-month">Past Month</SelectItem>
                      {/* <SelectItem value="week-over-week">
                      Week over Week
                    </SelectItem>
                    <SelectItem value="day-over-day">Day over Day</SelectItem> */}
                    </SelectContent>
                  </Select>
                </Label>

                {/* Supplier Filter */}
                <Label vertical>
                  Supplier
                  <SupplierAutocomplete
                    supplierId={filters.supplierId}
                    onSelect={(supplierId) =>
                      updateFilter('supplierId', supplierId)
                    }
                  />
                </Label>

                <Label vertical>
                  Product
                  <ProductsV2Autocomplete
                    selectedProductId={filters.productId}
                    onProductSelect={(productId) =>
                      updateFilter('productId', productId)
                    }
                    allowClear
                  />
                </Label>

                <Label vertical>
                  Product Match
                  <Select
                    value={filters.productMatch ?? 'matched'}
                    onValueChange={(value) =>
                      updateFilter('productMatch', value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="matched">Matched</SelectItem>
                      <SelectItem value="partial">Partial</SelectItem>
                      <SelectItem value="unmatched">Unmatched</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                </Label>

                {/* Custom Date Range */}
                {/* <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label vertical>From Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateFrom
                          ? format(filters.dateFrom, 'PPP')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateFrom}
                        onSelect={(date) => updateFilter('dateFrom', date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="dateTo">To Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.dateTo
                          ? format(filters.dateTo, 'PPP')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateTo}
                        onSelect={(date) => updateFilter('dateTo', date)}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div> */}

                {/* Clear Filters */}
                <Button
                  variant="outline"
                  onClick={() => setFilters({})}
                  className="flex gap-1"
                  disabled={Object.keys(filters).length === 0}
                >
                  <Reset />
                  Reset Filters
                </Button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {/* <div className="flex justify-between items-center">
              <div className="flex items-center gap-2"></div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"

                    size="icon"
                    onClick={() => setShowHistoricalData(!showHistoricalData)}
                    className="text-xs p-0"
                    disabled
                  >
                    {showHistoricalData ? (
                      <ChevronsRightLeft />
                    ) : (
                      <ChevronsLeftRight />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {showHistoricalData
                    ? 'Hide Historical Data'
                    : 'Show Historical Data'}
                </TooltipContent>
              </Tooltip>
            </div> */}

            <div className="relative overflow-auto max-h-[calc(100vh-200px)]">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                  <TableRow>
                    <TableHead className="w-[300px] bg-background">
                      <div className="flex gap-2">
                        <Label>
                          Breakdown by
                          <ProductVariantBreakdown />
                        </Label>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  expandAllAggregates(allAggregateKeys)
                                }
                                className="text-xs p-0"
                              >
                                <ArrowsOutIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>Expand All Rows</TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={collapseAllAggregates}
                                className="text-xs p-0"
                              >
                                <ArrowsInIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>Collapse All Rows</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="inline-flex">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  if (sortBy === 'alphabetical') return;
                                  setSortBy('alphabetical');
                                }}
                                className={cn(
                                  'text-xs p-0 cursor-not-allowed',
                                  sortBy !== 'alphabetical' &&
                                    'bg-muted text-muted-foreground cursor-pointer',
                                )}
                              >
                                <ArrowDownIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sortBy === 'alphabetical'
                              ? 'Sorting Alphabetically'
                              : 'Sort Alphabetically'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                    {/* Price trend graph column hidden per GEN-830 */}
                    {/* <TableHead className="w-[100px] border-l border-stone-300 bg-background">
                      Price Trend (
                      {timePeriod === 'past-week' ? 'Past Week' : 'Past Month'})
                    </TableHead> */}
                    {showHistoricalData && (
                      <>
                        {/* TODO make these dynamic based on date range */}
                        <TableHead className="w-[100px] text-center bg-background">
                          7 Days Ago
                        </TableHead>
                        <TableHead className="w-[100px] text-center bg-background">
                          2 Days Ago
                        </TableHead>
                        <TableHead className="w-[100px] text-center bg-background">
                          Yesterday
                        </TableHead>
                      </>
                    )}
                    <TableHead className="w-[150px] border-l border-stone-300 bg-background">
                      <div className="flex justify-center items-center gap-2">
                        <span>FOB Today</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                'text-xs p-0',
                                sortBy === 'alphabetical' &&
                                  'bg-muted text-muted-foreground',
                              )}
                              onClick={() => {
                                if (sortBy === 'price-asc') {
                                  setSortBy('price-desc');
                                  return;
                                }
                                setSortBy('price-asc');
                              }}
                            >
                              {sortBy !== 'price-asc' ? (
                                <SortDescendingIcon />
                              ) : (
                                <SortAscendingIcon />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {sortBy === 'price-asc'
                              ? 'Sort High to Low'
                              : sortBy === 'price-desc'
                                ? 'Sort Low to High'
                                : 'Sort by Price'}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transformedData?.map((aggregatedRowData) => (
                    <QuoteSheetDashboardRow
                      key={aggregatedRowData.key}
                      data={aggregatedRowData}
                      aggregateKeyPrefix={[]}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
